import React from 'react';
import {
  Flex,
  Icon,
  Button,
  Tooltip,
  MenuItem,
  Box,
  useBreakpointValue,
} from '@chakra-ui/react';
import { KText } from '@/ui-library/components/text';
import { KTag } from '@/ui-library/components';
import { KModal } from '@/ui-library/components/molecules/KModal/KModal';
import { RequisitionStatusTag } from './RequisitionStatusTag';
import { PartOfGroupTag } from './PartOfGroupTag';
import { Edit05 } from '@kula-ai/icons/outline/Edit05';
import dayjs from 'dayjs';
import useUrlState from '@ahooksjs/use-url-state';
import { Clock } from '@kula-ai/icons/dual-tone/Clock';
import { KMenuButton } from '@/ui-library/components/atoms/KMenuButton/KMenuButton';
import { useMutation } from '@tanstack/react-query';
import { API_SERVICE, API_KEYS } from '../api';
import { useShowToast } from '@/hooks';
import {
  transformRequisitionToFormValues,
  generateRequisitionFormData,
} from '../utils/requisitionFormHelpers';

/**
 * Converts a requisition code to group format by replacing the last segment with 'X'
 * Example: "VV-397-2" -> "VV-397-X"
 */
const getGroupCodeFormat = (code?: string): string => {
  if (!code) return '';
  const parts = code.split('-');
  if (parts.length > 1) {
    parts[parts.length - 1] = 'X';
    return parts.join('-');
  }
  return code;
};

interface DaysOpenIndicatorProps {
  openedAt: string;
}

const DaysOpenIndicator: React.FC<DaysOpenIndicatorProps> = ({ openedAt }) => {
  const isMobile = useBreakpointValue({ base: true, md: false });
  const [isTooltipOpen, setIsTooltipOpen] = React.useState(false);
  const daysOpen = dayjs().diff(dayjs(openedAt), 'day');

  const displayText =
    daysOpen < 1
      ? 'less than a day'
      : `${daysOpen} ${daysOpen === 1 ? 'day' : 'days'}`;

  const tagContent = (
    <KTag
      value={
        <Flex alignItems="center" gap="4px">
          <Icon as={Clock} boxSize="14px" color="secondary-500" />
          <KText>
            <KText as="span" color="secondary-900" fontWeight="bold">
              Days open:
            </KText>{' '}
            {displayText}
          </KText>
        </Flex>
      }
      width="max-content"
    />
  );

  // On mobile, use controlled tooltip that opens on click
  if (isMobile) {
    return (
      <Tooltip
        label="Days open is calculated from the open date to the current date"
        hasArrow
        isOpen={isTooltipOpen}
      >
        <Box onClick={() => setIsTooltipOpen(!isTooltipOpen)}>{tagContent}</Box>
      </Tooltip>
    );
  }

  return (
    <Tooltip
      label="Days open is calculated from the open date to the current date"
      hasArrow
      shouldWrapChildren
    >
      {tagContent}
    </Tooltip>
  );
};

interface TimeToFillIndicatorProps {
  openedAt: string;
  filledAt: string;
}

const TimeToFillIndicator: React.FC<TimeToFillIndicatorProps> = ({
  openedAt,
  filledAt,
}) => {
  const timeToFill = dayjs(filledAt).diff(dayjs(openedAt), 'day');

  const displayText =
    timeToFill < 1
      ? 'less than a day'
      : `${timeToFill} ${timeToFill === 1 ? 'day' : 'days'}`;

  return (
    <Tooltip
      label="Time to fill is calculated from the open date to the date the requisition was filled"
      hasArrow
      shouldWrapChildren
    >
      <KTag
        value={
          <Flex alignItems="center" gap="4px">
            <Icon as={Clock} boxSize="14px" color="secondary-500" />
            <KText>
              <KText as="span" color="secondary-900" fontWeight="bold">
                Time to fill:
              </KText>{' '}
              {displayText}
            </KText>
          </Flex>
        }
        width="max-content"
      />
    </Tooltip>
  );
};

interface RequisitionHeaderProps {
  requisition: any;
  isEditMode: boolean;
  readOnlyMode: boolean;
  isReqAssociated?: boolean;
  canEdit?: boolean;
  onEditClick?: () => void;
  onStatusChange: (action: string) => void;
  onApprovalChange?: () => void;
  onClose?: () => void;
  queryClient: any;
  requisitionId: string;
  openMenuByDefault?: boolean;
  isPartOfGroup?: boolean;
  availableRequisitions?: any[];
  allGroupRequisitions?: any[];
  applyToGroup?: boolean;
  isGrouped?: boolean;
}

export const RequisitionHeader: React.FC<RequisitionHeaderProps> = ({
  requisition,
  isEditMode,
  readOnlyMode,
  isReqAssociated = false,
  canEdit = false,
  onEditClick,
  onStatusChange,
  onApprovalChange,
  onClose,
  queryClient,
  requisitionId,
  openMenuByDefault = false,
  isPartOfGroup = false,
  availableRequisitions = [],
  allGroupRequisitions = [],
  applyToGroup = false,
  isGrouped = false,
}) => {
  const [, setUrlState] = useUrlState();
  const toast = useShowToast();

  const groupRequisitionCodes = React.useMemo(() => {
    if (allGroupRequisitions.length > 0) {
      return allGroupRequisitions
        .map((req) => req.code)
        .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
    }
    return [];
  }, [allGroupRequisitions]);

  const [showEditGroupConfirmModal, setShowEditGroupConfirmModal] =
    React.useState(false);

  // Mutation for updating requisition to remove filled/associated reqs from group
  const updateRequisitionMutation = useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: Record<string, any>;
    }) => API_SERVICE.updateRequisition(id, payload),
  });

  const editMenuItems = React.useMemo(() => {
    return [
      {
        id: 'edit_single',
        label: 'Edit this requisition alone',
        description:
          'Editing this requisition alone will detach it from the group and assign a new requisition ID to it',
        applyToGroup: false,
      },
      {
        id: 'edit_all',
        label: 'Edit all requisitions in the group',
        description: '',
        applyToGroup: true,
        groupRequisitionCodes: groupRequisitionCodes,
      },
    ];
  }, [groupRequisitionCodes]);

  // Check if any requisition in the group is filled or has an association
  const hasFilledOrAssociatedReqs = React.useMemo(() => {
    if (!allGroupRequisitions || allGroupRequisitions.length === 0)
      return false;
    return allGroupRequisitions.some(
      (req) => req.status === 'filled' || req.requisition_association?.id
    );
  }, [allGroupRequisitions]);

  const handleEditClick = (applyToGroup = false) => {
    // If editing all in a group with filled/associated requisitions, show confirmation modal
    if (applyToGroup && hasFilledOrAssociatedReqs) {
      setShowEditGroupConfirmModal(true);
      return;
    }

    // Proceed with edit
    if (requisition) {
      if (applyToGroup) {
        setUrlState({
          edit: requisition.id.toString(),
          apply_to_group: 'true',
          view: undefined, // Close drawer when opening edit modal
        });
      } else {
        // Regular edit - use onEditClick if available, otherwise set URL state
        if (onEditClick) {
          onEditClick();
        } else {
          setUrlState({
            edit: requisition.id.toString(),
          });
        }
      }
    }
  };

  const confirmEditGroup = async () => {
    if (!requisition) return;

    try {
      // Step 1: Calculate head_count excluding filled and associated requisitions
      const validRequisitions = allGroupRequisitions.filter(
        (req) => req.status !== 'filled' && !req.requisition_association?.id
      );
      const adjustedHeadCount = validRequisitions.length;

      // Step 2: Transform requisition data to form values
      const formValues = transformRequisitionToFormValues(requisition);

      // Step 3: Generate payload from form values (similar to EditRequisitions)
      const payload = generateRequisitionFormData(
        formValues as any,
        requisition
      );

      // Step 4: Add apply_to_group flag and adjusted head_count
      payload.apply_to_group = true;
      payload.head_count = adjustedHeadCount;

      // Step 5: Make update API call
      await updateRequisitionMutation.mutateAsync({
        id: requisition.id.toString(),
        payload,
      });

      // Invalidate queries to refresh data
      queryClient.invalidateQueries({
        queryKey: [API_KEYS.getRequisitions],
      });
      queryClient.invalidateQueries({
        queryKey: [API_KEYS.getRequisition, requisition.id.toString()],
      });

      toast({
        title: 'Group updated successfully',
        description:
          'Filled and associated requisitions have been removed from the group',
      });

      // Close modal
      setShowEditGroupConfirmModal(false);

      // Open edit drawer
      setUrlState({
        edit: requisition.id.toString(),
        apply_to_group: 'true',
        view: undefined,
      });
    } catch (error: any) {
      console.error('Update error:', error);
      toast({
        title: 'Failed to update group',
        description: error?.message || 'Please try again.',
        status: 'error',
      });
    }
  };

  const handleStatusChange = (action: string) => {
    if (action === 'reopen' && onClose) {
      onClose();
      setUrlState({
        view: undefined,
        draft: requisition?.id.toString(),
      });
      return;
    }
    onStatusChange(action);
  };

  const reqOpenedState =
    requisition?.opened_at && requisition?.status === 'open';

  const reqFilledState =
    requisition?.filled_at && requisition?.status === 'filled';

  // Determine header title based on edit mode and group status
  const getHeaderTitle = () => {
    if (!isEditMode) {
      // If it's a grouped requisition from approvals, show code with "X" format
      if (isGrouped && requisition?.code) {
        return getGroupCodeFormat(requisition.code);
      }
      return requisition?.code;
    }

    switch (true) {
      case applyToGroup:
        return 'Edit group of requisitions';

      case isPartOfGroup:
        return `Edit requisition (formerly ${requisition?.code})`;

      default:
        return requisition?.code;
    }
  };

  const headerTitle = getHeaderTitle();

  return (
    <>
      <Flex
        justifyContent="space-between"
        alignItems="center"
        w="100%"
        py={{ base: '12px', md: '1rem' }}
        pr={{ base: '12px', md: '1rem' }}
        pl={{
          base: readOnlyMode ? '56px' : '12px',
          md: '24px',
        }}
        gap={{ base: '8px', md: '8px' }}
        borderBottom="1px solid"
        borderColor="border-color"
      >
        <Flex alignItems="center" gap={3}>
          <KText
            variant="heading_2"
            color="secondary-900"
            maxW={
              (reqOpenedState || reqFilledState) && isPartOfGroup
                ? '80px'
                : 'fit-content'
            }
            isTruncated
            enableTooltip={true}
          >
            {headerTitle}
          </KText>
          {requisition?.confidential && !isEditMode && (
            <KTag
              width="fit-content"
              value="Confidential"
              maxW={
                (reqOpenedState || reqFilledState) && isPartOfGroup
                  ? '60px'
                  : 'fit-content'
              }
              isTruncated
              enableTooltip={true}
            />
          )}
          {requisition && isPartOfGroup && (
            <PartOfGroupTag
              requisition={requisition}
              allRequisitions={availableRequisitions}
            />
          )}
          <RequisitionStatusTag
            status={requisition?.status}
            requisitionId={requisition?.id}
            showApprovalMenu={!readOnlyMode}
            showActionMenu={!readOnlyMode}
            onStatusChange={handleStatusChange}
            onApprovalChange={onApprovalChange}
            requisition={requisition}
            openMenuByDefault={openMenuByDefault}
            allRequisitions={availableRequisitions}
            isPartOfGroup={isPartOfGroup}
            allGroupRequisitions={allGroupRequisitions}
          />
          {reqOpenedState && (
            <DaysOpenIndicator openedAt={requisition.opened_at} />
          )}
          {requisition?.status === 'filled' &&
            requisition?.opened_at &&
            requisition?.filled_at && (
              <TimeToFillIndicator
                openedAt={requisition.opened_at}
                filledAt={requisition.filled_at}
              />
            )}
        </Flex>
        {!isEditMode && !readOnlyMode && canEdit && (
          <Tooltip
            label={
              isReqAssociated && requisition?.status === 'filled'
                ? 'Filled requisitions cannot be edited. Delete the associated offer or mark the candidate as rejected to edit this requisition.'
                : isReqAssociated && requisition?.status === 'open'
                ? "This requisition is associated with the candidate's offer and cannot be edited. Delete the offer to make changes."
                : ''
            }
            shouldWrapChildren
            hasArrow
            isDisabled={!isReqAssociated}
          >
            {isPartOfGroup && !isReqAssociated ? (
              <KMenuButton
                variant="secondary"
                triggerContent={
                  <Box>
                    <Flex alignItems="center" gap="6px">
                      <Icon as={Edit05} />
                      <KText color="primary-500">Edit</KText>
                    </Flex>
                  </Box>
                }
                menuContentProps={{
                  minW: '300px',
                  maxW: '320px',
                  boxShadow: 'drop-shadows-bottom',
                  zIndex: 1500,
                  borderRadius: '6px',
                  p: '0',
                  onClick: (e: any) => {
                    e.stopPropagation();
                    e.preventDefault();
                  },
                }}
                dividerProps={{ display: 'none' }}
                menuContent={
                  <>
                    {editMenuItems.map((item, index) => (
                      <MenuItem
                        key={item.id}
                        onClick={() => handleEditClick(item.applyToGroup)}
                        p="12px"
                        borderTop={index > 0 ? '1px solid' : undefined}
                        borderColor={index > 0 ? 'border-color' : undefined}
                        borderRadius="0"
                        role="group"
                        className="group"
                      >
                        <Flex direction="column" gap="6px">
                          <Flex direction="column" gap="3px">
                            <KText size="sm-medium" color="secondary-900">
                              {item.label}
                            </KText>
                            {item.description && (
                              <KText size="sm-regular" color="secondary-700">
                                {item.description}
                              </KText>
                            )}
                          </Flex>
                          {item.applyToGroup &&
                            item.groupRequisitionCodes &&
                            item.groupRequisitionCodes.length > 0 && (
                              <Flex
                                flexWrap="wrap"
                                gap="6px"
                                maxH="200px"
                                overflowY="auto"
                              >
                                {item.groupRequisitionCodes.map(
                                  (code: string) => (
                                    <KTag
                                      key={code}
                                      value={code}
                                      color="secondary-700"
                                      _groupHover={{
                                        bgColor: 'white',
                                        borderColor: 'border-color',
                                        color: 'secondary-700',
                                        borderWidth: '1px',
                                      }}
                                    />
                                  )
                                )}
                              </Flex>
                            )}
                        </Flex>
                      </MenuItem>
                    ))}
                  </>
                }
                isPortal={true}
              />
            ) : (
              <Button
                variant="secondary"
                leftIcon={<Icon as={Edit05} />}
                onClick={onEditClick}
                isDisabled={isReqAssociated}
              >
                Edit
              </Button>
            )}
          </Tooltip>
        )}
      </Flex>

      {/* Confirmation Modal for editing groups with filled/associated requisitions */}
      <KModal
        isOpen={showEditGroupConfirmModal}
        onClose={() => {
          if (!updateRequisitionMutation.isPending) {
            setShowEditGroupConfirmModal(false);
          }
        }}
        title={`Edit ${getGroupCodeFormat(requisition?.code)}?`}
        ctaAction={confirmEditGroup}
        ctaLabel="Confirm"
        isCtaLoading={updateRequisitionMutation.isPending}
        loadingText="Updating..."
        closeOnOverlayClick={!updateRequisitionMutation.isPending}
        closeOnEsc={!updateRequisitionMutation.isPending}
        size="md"
        modalContentProps={{
          maxW: '480px',
          borderRadius: '8px',
        }}
        modalHeaderProps={{
          p: '20px 24px 16px 24px',
        }}
        titleProps={{
          size: 'lg-semi-bold',
        }}
        modalBodyProps={{
          p: '0 24px 24px 24px',
        }}
        modalFooterProps={{
          p: '16px 24px',
          gap: '8px',
        }}
      >
        <KText size="sm-regular" color="secondary-700">
          {getGroupCodeFormat(requisition?.code)} includes requisitions that are
          filled or associated with offers. If you edit this group, those
          requisitions will be removed from the group and assigned new
          requisition IDs. Do you want to continue?
        </KText>
      </KModal>
    </>
  );
};
